'use client'
import Section from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Typography } from 'components/Contentful'
import { useLazyQuery, gql } from '@apollo/client'
import { useEffect } from 'react'
import { formatPublishDate } from 'utils/conversions'
import { STORY_TYPES } from 'constants/storyTypes'
import { PRESS_ROUTE } from 'constants/navigation'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { removePageFromQuery } from 'utils/next/routes'
import ApolloClientProvider from 'providers/ApolloClientProvider'

const GET_PRESS_STORY = gql`
  query pressStory($filter: PressStoryFilter!) {
    pressStoryCollection(limit: 6, skip: 0, order: publishDate_DESC, where: $filter) {
      total
      items {
        slug
        title
        publishDate
        externalLink
        storyType
      }
    }
  }
`

function LatestArticlesModule({ title, config, initialArticles }) {
  const hasServerData = Array.isArray(initialArticles) && initialArticles.length > 0
  const [fetchPressStories, { data }] = useLazyQuery(GET_PRESS_STORY, {
    variables: {
      filter: config?.jsonContent?.filter || {},
    },
  })
  const articles = hasServerData ? initialArticles : data?.pressStoryCollection?.items
  const { query } = useAppRouter()
  useEffect(() => {
    if (hasServerData) return
    try {
      fetchPressStories({ variables: {} })
    } catch (error) {
      console.error('Error fetching press stories', error)
    }
  }, [fetchPressStories, hasServerData])

  const backgroundConfig = config?.jsonContent?.sectionBackground ?? 'bgDefault'
  const forceRedirect = config?.jsonContent?.forceRedirect
  return (
    <Section
      contain={true}
      variant={backgroundConfig}
      className="py-12"
      data-testid="latest-articles-section"
    >
      <div className="flex flex-col lg:flex-row items-center justify-between p-4 ">
        {title && (
          <Typography
            content={title?.textContent}
            variant="headingMd"
            mobileVariant="displaySm"
            responsive={true}
            className="text-center lg:text-left"
            data-testid="latest-articles-section-title"
          />
        )}
      </div>
      {articles?.length > 0 && (
        <div className=" px-6 ">
          {articles.map((article, idx) => {
            return (
              <a
                target={
                  article?.storyType === STORY_TYPES.IN_THE_NEWS && !forceRedirect
                    ? '_blank'
                    : '_self'
                }
                rel={article?.storyType === STORY_TYPES.IN_THE_NEWS ? 'noreferrer nofollow' : ''}
                href={
                  forceRedirect
                    ? forceRedirect
                    : article?.storyType === STORY_TYPES.IN_THE_NEWS && article?.externalLink
                    ? `${article?.externalLink}`
                    : `${PRESS_ROUTE}/${article?.slug}${
                        Object.keys(query).length === 0 ? '' : `?${removePageFromQuery(query)}`
                      }`
                }
                key={idx}
                className="no-underline "
                data-testid={`latest-articles-section-article-link-${idx}`}
              >
                <div
                  className={`py-5 border-x-0 border-t-0 border-solid border-gray-200 ${
                    idx + 1 !== articles.length ? 'border-b-2' : 'border-b-0'
                  }`}
                  data-testid={`latest-articles-section-article-container-${idx}`}
                >
                  {article?.storyType && (
                    <Typography
                      content={article?.storyType}
                      variant="labelSm"
                      className="pb-2 font-bold! text-blue-300 uppercase tracking-eyebrow"
                      data-testid="latest-articles-section-article-story-type"
                    />
                  )}
                  {article?.title && (
                    <Typography
                      content={article?.title}
                      responsive={true}
                      variant="headingMd"
                      mobileVariant="bodySm"
                      className="pb-2  text-gray-900"
                      data-testid="latest-articles-section-article-title"
                    />
                  )}
                  {article?.publishDate && (
                    <Typography
                      content={formatPublishDate(article?.publishDate)}
                      variant="labelSm"
                      className=" hidden md:block  text-gray-600"
                      fontWeight="regular"
                      data-testid="latest-articles-section-article-publish-date"
                    />
                  )}
                </div>
              </a>
            )
          })}
        </div>
      )}
    </Section>
  )
}

function LatestArticles({ content, initialArticles }) {
  const { title, config } = useMemoizedContentGetter(content, ['title', 'config'])

  return (
    // TODO: Maybe we initialize the provider on the page.tsx level if we know it renders a client component that uses apollo queries
    <ApolloClientProvider>
      <LatestArticlesModule title={title} config={config} initialArticles={initialArticles} />
    </ApolloClientProvider>
  )
}

export { LatestArticles, LatestArticlesModule, GET_PRESS_STORY }
export default LatestArticles
